import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import Big from 'big.js';
import { PaddlePricesResponse } from '../../../../../paddle/models/PaddlePricesResponse';
import { PaddleDefaultPrices, PaddleProductId } from '../../../../../paddle';
import { PlaylistsSharingPremiumPricingElement } from './PlaylistsSharingPremiumPricingElement';
import { premiumFeaturesArtist, premiumFeaturesBasic, premiumFeaturesInfluencer } from '../premiumFeatures';

function productIdToTitle(productId: PaddleProductId) {
  switch (productId) {
    case PaddleProductId.SmartLinksRookie:
      return 'Rookie';
    case PaddleProductId.SmartLinksInfluencer:
      return 'Influencer';
    case PaddleProductId.SmartLinksArtist:
      return 'Artist';
    default:
      return '';
  }
}

function productIdToSmartLinksLimit(productId: PaddleProductId) {
  switch (productId) {
    case PaddleProductId.SmartLinksRookie:
      return 10;
    case PaddleProductId.SmartLinksInfluencer:
      return 50;
    case PaddleProductId.SmartLinksArtist:
      return 150;
    default:
      return 10;
  }
}

function productIdToAccountsLimit(productId: PaddleProductId) {
  switch (productId) {
    case PaddleProductId.SmartLinksRookie:
      return 1;
    case PaddleProductId.SmartLinksInfluencer:
      return 3;
    case PaddleProductId.SmartLinksArtist:
      return 10;
    default:
      return 1;
  }
}

export const PlaylistsSharingPremiumPricingElementSubscription: React.FC<{
  productId: PaddleProductId;
  isPromoted?: boolean;
  paddlePricing: PaddlePricesResponse | null;
}> = ({ productId, paddlePricing, isPromoted = false }) => {
  const intl = useIntl();

  const subTitle = useMemo(() => {
    switch (productId) {
      case PaddleProductId.SmartLinksRookie:
        return intl.formatMessage({
          id: 'sml.web.playlists-sharing.subtitle.rookie',
          defaultMessage: "Perfect for Individual's online social presence"
        });
      case PaddleProductId.SmartLinksInfluencer:
        return intl.formatMessage({
          id: 'sml.web.playlists-sharing.subtitle.influencer',
          defaultMessage: 'For Professionals to engage with their audience'
        });
      case PaddleProductId.SmartLinksArtist:
        return intl.formatMessage({
          id: 'sml.web.playlists-sharing.subtitle.artist',
          defaultMessage: 'Great for Teams and Agencies to engage, promote, analyze & share to their fans'
        });
      default:
        return '';
    }
  }, [intl, productId]);

  const allFeatures = useMemo(() => {
    switch (productId) {
      case PaddleProductId.SmartLinksArtist:
        return premiumFeaturesArtist;
      case PaddleProductId.SmartLinksInfluencer:
        return premiumFeaturesInfluencer;
      default:
        return premiumFeaturesBasic;
    }
  }, [productId]);

  const translatedFeatures = useMemo(() => {
    return allFeatures.map((feature) => ({
      label: intl.formatMessage(feature.label, {
        smartLinksLimits: productIdToSmartLinksLimit(productId),
        accountsLimits: productIdToAccountsLimit(productId),
        b: (str: React.ReactNode[]) => <strong>{str}</strong>,
        fym: (str: React.ReactNode[]) => (
          <a href="https://freeyourmusic.com" target="_blank" rel="noopener noreferrer">
            {str}
          </a>
        )
      }),
      icon: feature.icon
    }));
  }, [allFeatures, intl, productId]);
  const price =
    paddlePricing?.productsById?.[productId]?.subscription?.price.gross ?? PaddleDefaultPrices[productId].price;
  const currency = paddlePricing?.productsById?.[productId]?.currency ?? PaddleDefaultPrices[productId].currency;

  const monthlyPrice = useMemo(() => {
    if (productId === PaddleProductId.SmartLinksRookie) {
      return intl.formatMessage({
        id: 'sml.web.premium.free',
        defaultMessage: 'FREE'
      });
    }
    return intl
      .formatNumber(new Big(price).toNumber(), {
        currency,
        style: 'currency'
      })
      .replace('.00', '');
  }, [currency, intl, price, productId]);

  return (
    <PlaylistsSharingPremiumPricingElement
      outline={!isPromoted}
      ctaLabel={intl.formatMessage({
        id: 'sml.web.premium.get-started',
        defaultMessage: 'Try for FREE!'
      })}
      label={
        isPromoted
          ? intl.formatMessage({
              id: 'sml.web.playlists-sharing.payment.label',
              defaultMessage: 'Most popular'
            })
          : undefined
      }
      title={productIdToTitle(productId)}
      price={monthlyPrice}
      subtitle={
        <>
          {intl.formatMessage({
            id: 'sml.web.playlists-sharing.payment.per-month',
            defaultMessage: 'per month'
          })}
          <br />
          <br />
          <span style={{ fontSize: '1rem', fontWeight: 500 }}>{subTitle}</span>
        </>
      }
      listTitle={intl.formatMessage({
        id: 'sml.web.premium.premium.listTitle',
        defaultMessage: 'Features'
      })}
      listElements={translatedFeatures}
    />
  );
};
